"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { mutate } from "swr"
import { ShieldCheck } from "@/lib/icons"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useTranslations } from "next-intl"

/** Group ids as the server sends them (lib/platform-block.ts); names and descriptions come from the translations. */
const GROUP_TEXT_KEYS: Record<string, { name: string; desc: string }> = {
  minor: { name: "platformGroupMinor", desc: "platformGroupMinorDesc" },
  gore: { name: "platformGroupGore", desc: "platformGroupGoreDesc" },
  nonconsent: { name: "platformGroupNonconsent", desc: "platformGroupNonconsentDesc" },
  feral: { name: "platformGroupFeral", desc: "platformGroupFeralDesc" },
  vore: { name: "platformGroupVore", desc: "platformGroupVoreDesc" },
  scat: { name: "platformGroupScat", desc: "platformGroupScatDesc" },
}

/**
 * The per-category switches at the top of the Blacklist (lib/platform-block.ts, "THE SWITCH MODEL"). While the platform tags are locked, or for a
 * guest, this is only a note that the protection is on. Once unlocked, a signed-in adult sees two groups that are always hidden (no switch) and a
 * switch for each of the others, off by default; turning one on needs a date of birth on file and a confirmation.
 */
export function PlatformTagsRow() {
  const tt = useTranslations("blacklistManager")
  const [canChange, setCanChange] = useState(false)
  const [locked, setLocked] = useState(false)
  const [alwaysHidden, setAlwaysHidden] = useState<string[]>([])
  const [switchable, setSwitchable] = useState<string[]>([])
  const [shown, setShown] = useState<string[]>([])
  const [hasBirthDate, setHasBirthDate] = useState(false)
  const [needsBirthDate, setNeedsBirthDate] = useState(false)
  const [confirmGroup, setConfirmGroup] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const apply = (data: any) => {
    setCanChange(data.canChange === true)
    setLocked(data.locked === true)
    setAlwaysHidden(Array.isArray(data.alwaysHidden) ? data.alwaysHidden : [])
    setSwitchable(Array.isArray(data.switchable) ? data.switchable : [])
    setShown(Array.isArray(data.shown) ? data.shown : [])
    setHasBirthDate(data.hasBirthDate === true)
  }

  useEffect(() => {
    let alive = true
    fetch("/api/settings/platform-tags")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) apply(data)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  const save = async (next: string[]) => {
    setSaving(true)
    setFailed(false)
    setNeedsBirthDate(false)
    try {
      const response = await fetch("/api/settings/platform-tags", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shown: next }),
      })
      if (response.status === 403) {
        const body = await response.json().catch(() => ({}))
        if (body?.code === "birth_date_required") {
          setNeedsBirthDate(true)
          return
        }
      }
      if (!response.ok) throw new Error(String(response.status))
      apply(await response.json())
      // Results already on screen were filtered with the old setting: fetch them again.
      void mutate(() => true, undefined, { revalidate: true })
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const onToggle = (id: string, on: boolean) => {
    if (!canChange || saving) return
    if (!on) return void save(shown.filter((g) => g !== id))
    if (!hasBirthDate) return setNeedsBirthDate(true)
    setConfirmGroup(id)
  }

  const groupName = (id: string) => (GROUP_TEXT_KEYS[id] ? tt(GROUP_TEXT_KEYS[id].name) : id)
  const groupDesc = (id: string) => (GROUP_TEXT_KEYS[id] ? tt(GROUP_TEXT_KEYS[id].desc) : "")

  return (
    <div className="mb-4 rounded-md border p-3">
      <div className="flex items-center gap-2 min-w-0">
        <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium">{tt("platformTags")}</span>
      </div>

      {!canChange ? (
        <>
          <p className="mt-1 text-xs text-muted-foreground">{tt("platformTagsOnDesc")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{locked ? tt("platformTagsLocked") : tt("platformTagsGuest")}</p>
        </>
      ) : (
        <div className="mt-2 space-y-3">
          <p className="text-xs text-muted-foreground">{tt("platformSwitchesIntro")}</p>

          <div className="space-y-2">
            {alwaysHidden.map((id) => (
              <div key={id} className="space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm">{groupName(id)}</span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">{tt("platformAlwaysHidden")}</span>
                </div>
                <p className="text-xs text-muted-foreground">{groupDesc(id)}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3 border-t pt-3">
            <p className="text-xs text-muted-foreground">{tt("platformSwitchableTitle")}</p>
            {switchable.map((id) => (
              <div key={id} className="space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`platform-group-${id}`} className="cursor-pointer text-sm font-normal">{groupName(id)}</Label>
                  <Switch
                    id={`platform-group-${id}`}
                    checked={shown.includes(id)}
                    disabled={saving}
                    onCheckedChange={(on) => onToggle(id, on)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{groupDesc(id)}</p>
              </div>
            ))}
            {(needsBirthDate || !hasBirthDate) && (
              <p className="text-xs text-muted-foreground">
                {tt("platformNeedsBirthDate")}{" "}
                <Link href="/settings?tab=account" className="underline">{tt("platformOpenSettings")}</Link>
              </p>
            )}
            {failed && <p className="text-xs text-destructive">{tt("platformTagsSaveFailed")}</p>}
          </div>
        </div>
      )}

      <AlertDialog open={confirmGroup !== null} onOpenChange={(open) => !open && setConfirmGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tt("platformSwitchWarnTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{tt("platformSwitchWarnBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tt("platformKeepHidden")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = confirmGroup
                setConfirmGroup(null)
                if (id) void save(Array.from(new Set([...shown, id])))
              }}
            >
              {tt("platformShowIt")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
